import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, Button } from 'antd';
import { Provider } from 'react-redux';
import { connect } from 'react-redux';
import { hideModal } from '../store/actions';
import { Icon, ThemeProvider } from 'dlweb-components';
import Modals from '../components/modals';
import { useResponsiveModal } from '../hooks';

// this renders a single modal instance
const ModalInstance = ({ store, $rootScope, modal, hideModal, zIndexBase }) => {
  const { id, modalType, modalProps = {}, modalContentProps: rawModalContentProps = {} } = modal;

  const [open, setOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const modalContentProps = { ...rawModalContentProps };

  const { x, y, width, height } = useResponsiveModal(
    undefined,
    undefined,
    modalProps.minWidth ?? 915,
    modalProps.maxWidth ?? 1500
  );

  const closeModal = useCallback(() => {
    setOpen(false);
    setIsFullscreen(false);
  }, []);

  const afterClose = useCallback(() => {
    // notify redux to remove this modal by id
    hideModal(modalType);
  }, [hideModal, modalType]);

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  const shouldBeResponsive = modalProps?.responsive || isFullscreen;

  const responsiveProps = useMemo(() => {
    if (!shouldBeResponsive) return {};
    return {
      width: width,
      style: {
        top: y,
        left: x,
        height: height,
        width: width,
        minWidth: '915px',
        position: 'absolute',
      },
      styles: {
        body: { height: 'calc(100% - 25px)' },
      },
    };
  }, [shouldBeResponsive, width, height, x, y]);

  const modalOptions = useMemo(
    () => ({
      open,
      onOk: closeModal,
      onCancel: closeModal,
      afterClose,
      destroyOnClose: true,
      forceRender: true,
      maskClosable: false,
      footer: null,
    }),
    [open, closeModal, afterClose]
  );

  // inject working folder like before
  modalContentProps.workingFolder = $rootScope.workingFolder;

  const mergedModalProps = {
    ...modalProps,
    zIndex: modalProps.zIndex ?? zIndexBase,
  };

  // update profile title with fullscreen toggle
  if (modalType === 'UPDATE_PROFILE') {
    mergedModalProps.title = (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Update Profile</span>
        <Button
          type="text"
          style={{
            width: '32px',
            fontSize: '12px',
            color: 'rgba(0, 0, 0, 0.45)',
            position: 'absolute',
            right: '45px',
            top: '12px',
          }}
          onClick={toggleFullscreen}
        >
          <Icon faStyle="fas" type={isFullscreen ? 'compress-alt' : 'expand-alt'} />
        </Button>
      </div>
    );
  }

  // ---- error modal ----
  if (modalType === 'ERROR') {
    return (
      <Modal
        {...modalOptions}
        cancelButtonProps={{ hidden: true }}
        {...mergedModalProps}
      >
        <div style={{ display: 'flex' }}>
          <div style={{ width: '50px', color: '#ff4d4f' }}>
            <Icon type="exclamation-circle" />
          </div>
          <div dangerouslySetInnerHTML={{ __html: modalContentProps.content }} />
        </div>
      </Modal>
    );
  }

  // ---- special import modal ----
  if (modalType === 'IMPORT') {
    const ImportModal = Modals[modalType] || null;

    const importModalOptions = {
      open,
      afterClose,
      destroyOnClose: true,
      forceRender: true,
      maskClosable: false,
      footer: null,
    };

    return (
      <ThemeProvider>
        <ImportModal
          closeModal={closeModal}
          modalOptions={importModalOptions}
          modalProps={mergedModalProps}
          responsiveProps={responsiveProps}
          angularScope={$rootScope}
          {...modalContentProps}
        />
      </ThemeProvider>
    );
  }

  // ---- special new_document modal ----
  if (modalType === 'NEW_DOCUMENT') {
    const NewDocModal = Modals[modalType] || null;

    const newDocModalOptions = {
      open,
      afterClose,
      destroyOnClose: true,
      forceRender: true,
      maskClosable: false,
      footer: null,
    };

    return (
      <ThemeProvider>
        <NewDocModal
          closeModal={closeModal}
          modalOptions={newDocModalOptions}
          modalProps={mergedModalProps}
          responsiveProps={responsiveProps}
          angularScope={$rootScope}
          {...modalContentProps}
        />
      </ThemeProvider>
    );
  }

  // ---- all other default modals ----
  const ModalContents = Modals[modalType] || null;

  return (
    <ThemeProvider>
      <Modal
        {...modalOptions}
        {...mergedModalProps}
        {...responsiveProps}
      >
        {modalType && (
          <ModalContents
            hideModal={closeModal}
            angularScope={$rootScope}
            {...modalContentProps}
          />
        )}
      </Modal>
    </ThemeProvider>
  );
};

const ModalRoot = ({ store, $rootScope, open = [], hideModal }) => {
  if (!open.length) return null;

  return (
    <Provider store={store}>
      {open.map((modal, index) => (
        <ModalInstance
          key={modal.modalType || index}
          store={store}
          $rootScope={$rootScope}
          modal={modal}
          hideModal={hideModal}
          // slightly bump zindex for each stacked modal
          zIndexBase={1500 + index * 10}
        />
      ))}
    </Provider>
  );
};

// expect state.modal.modals to be an array
const mapStateToProps = (state) => state.modal;
const mapDispatchToProps = { hideModal };

export default connect(mapStateToProps, mapDispatchToProps)(ModalRoot);
